import { Add, ArrowForwardIos } from '@mui/icons-material';
import { Box, Button, IconButton } from '@mui/material';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import DashboardLayout from '../../Components/Dashboard/DashboardLayout/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { useFacility } from '../../context/FacilityContext';
import { db } from '../../firebase/config';
import styles from './Transportation.module.css';
import dayjs from 'dayjs';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import TransportationDialog from '../../Components/Transportation/TransportationDialog';
import FacilitySettingsDialog from '../../Components/Transportation/FacilitySettingsDialog';
import { useLoadScript } from '@react-google-maps/api';
import DevelopmentOnly from '../../Components/Dashboard/DevelopmentOnly/DevelopmentOnly';

const libraries = ['places'];

function Transportation() {
  const { facilityId } = useFacility();
  const { claims, userFirstName, userLastName } = useAuth();
  const [transportationAppointments, setTransportationAppointments] = useState(
    [],
  );
  const [isLoading, setIsLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('new');
  const [dialogFormData, setDialogFormData] = useState({
    id: null,
    transportationType: 'wheelchair',
    residentName: '',
    date: dayjs(),
    time: dayjs(),
    primaryInsurance: '',
    secondaryInsurance: '',
    reason: '',
    medicallyNecessary: '',
    roundTrip: '',
    selectedOption: '',
    destination: {},
  });

  const [viewMode, setViewMode] = useState('list');
  const [currentMonth, setCurrentMonth] = useState(dayjs().startOf('month'));
  const monthDays = useMemo(() => {
    const firstDayOfFirstWeek = currentMonth.startOf('month').startOf('week');
    const lastDayOfLastWeek = currentMonth.endOf('month').endOf('week');
    const daysCount = lastDayOfLastWeek.diff(firstDayOfFirstWeek, 'day') + 1;
    return Array.from({ length: daysCount }, (_, i) =>
      firstDayOfFirstWeek.add(i, 'day'),
    );
  }, [currentMonth]);

  useEffect(() => {
    setIsLoading(true);
    const q = query(
      collection(db, 'transportation'),
      where('facilityId', '==', facilityId),
      orderBy('date', 'asc'),
    );
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const events = querySnapshot.docs.map((docSnap) => ({
          ...docSnap.data(),
          id: docSnap.id,
        }));
        setTransportationAppointments(events);
        setIsLoading(false);
      },
      (error) => {
        console.error('Error fetching transportation appointments: ', error);
        setIsLoading(false);
      },
    );
    return () => unsubscribe();
  }, [facilityId]);

  const upcomingAppointments = transportationAppointments.filter((appt) => {
    const apptDate = dayjs(appt.date.toDate());
    const today = dayjs().startOf('day');
    const thirtyDaysFromToday = today.add(30, 'day');
    return (
      apptDate.isSame(today) ||
      (apptDate.isAfter(today) && apptDate.isBefore(thirtyDaysFromToday))
    );
  });

  const groupedUpcomingAppointments = upcomingAppointments.reduce(
    (groups, appt) => {
      const dayKey = dayjs(appt.date.toDate()).format('dddd M/D/YYYY');
      if (!groups[dayKey]) groups[dayKey] = [];
      groups[dayKey].push(appt);
      return groups;
    },
    {},
  );

  const sortedUpcomingDays = Object.keys(groupedUpcomingAppointments).sort(
    (a, b) => new Date(a) - new Date(b),
  );

  const groupedAppointments = transportationAppointments.reduce(
    (groups, appt) => {
      const dayKey = dayjs(appt.date.toDate()).format('dddd M/D/YYYY');
      if (!groups[dayKey]) groups[dayKey] = [];
      groups[dayKey].push(appt);
      return groups;
    },
    {},
  );
  const sortedDays = Object.keys(groupedAppointments).sort(
    (a, b) => new Date(a) - new Date(b),
  );
  const appointmentsByDate = transportationAppointments.reduce((acc, appt) => {
    const dateKey = dayjs(appt.date.toDate()).format('YYYY-MM-DD');
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(appt);
    return acc;
  }, {});

  const [canEdit, setCanEdit] = useState(false);
  useEffect(() => {
    const hasRequiredClaims = () => {
      if ('superadmin' in claims && claims.superadmin) return true;
      if ('admin' in claims && claims.admin) {
        if (claims.facilities?.includes(facilityId)) return true;
      }
      if ('staff' in claims && claims.staff) {
        if (claims.facilities?.includes(facilityId)) return true;
      }
      return false;
    };
    setCanEdit(hasRequiredClaims());
  }, [claims, facilityId]);

  const openDialog = (appointment = null) => {
    if (!canEdit) return;
    if (appointment) {
      setDialogMode('edit');
      setDialogFormData({
        id: appointment.id,
        transportationType: appointment.transportationType || 'wheelchair',
        residentName: appointment.residentName || '',
        date: appointment.date ? dayjs(appointment.date.toDate()) : dayjs(),
        time: appointment.time ? dayjs(appointment.time.toDate()) : dayjs(),
        primaryInsurance: appointment.primaryInsurance || '',
        secondaryInsurance: appointment.secondaryInsurance || '',
        reason: appointment.reason || '',
        medicallyNecessary:
          appointment.medicallyNecessary !== undefined
            ? appointment.medicallyNecessary
            : '',
        roundTrip:
          appointment.roundTrip !== undefined ? appointment.roundTrip : '',
        selectedOption: appointment.selectedOption || '',
        destination: appointment.destination || {},
      });
    } else {
      setDialogMode('new');
      setDialogFormData({
        id: null,
        transportationType: 'wheelchair',
        residentName: '',
        date: dayjs(),
        time: dayjs(),
        primaryInsurance: '',
        secondaryInsurance: '',
        reason: '',
        medicallyNecessary: '',
        roundTrip: '',
        selectedOption: '',
        destination: {},
      });
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
  };

  const handleCreateOrUpdate = async () => {
    setIsLoading(true);
    try {
      const { id, date, time, ...rest } = dialogFormData;
      const dateTimestamp = Timestamp.fromDate(date.startOf('day').toDate());
      const timeTimestamp = Timestamp.fromDate(
        date
          .hour(time.hour())
          .minute(time.minute())
          .second(0)
          .millisecond(0)
          .toDate(),
      );
      const payload = {
        ...rest,
        facilityId,
        date: dateTimestamp,
        time: timeTimestamp,
      };

      if (dialogMode === 'new') {
        payload.createdBy =
          userFirstName && userLastName
            ? `${userFirstName} ${userLastName}`
            : claims.displayName || 'Unknown';
        await addDoc(collection(db, 'transportation'), payload);
      } else if (dialogMode === 'edit' && id) {
        await updateDoc(doc(db, 'transportation', id), payload);
      }
    } catch (error) {
      console.error(
        'Error creating/updating transportation appointment: ',
        error,
      );
    } finally {
      setIsLoading(false);
      closeDialog();
    }
  };

  const handleDelete = async () => {
    if (!dialogFormData.id) return;
    setIsLoading(true);
    try {
      await deleteDoc(doc(db, 'transportation', dialogFormData.id));
    } catch (error) {
      console.error('Error deleting transportation appointment: ', error);
    } finally {
      setIsLoading(false);
      closeDialog();
    }
  };

  const [facilityDialogOpen, setFacilityDialogOpen] = useState(false);
  const [facilitySettings, setFacilitySettings] = useState(null);
  useEffect(() => {
    setFacilitySettings(null);
    const facilitySetupRef = doc(db, 'facilitySetup', facilityId);
    const unsubscribe = onSnapshot(
      facilitySetupRef,
      (docSnap) => {
        if (docSnap.exists()) {
          setFacilitySettings(docSnap.data());
        } else {
          setFacilitySettings(null);
        }
      },
      (error) => {
        console.error('Error fetching facility settings:', error);
      },
    );
    return () => unsubscribe();
  }, [facilityId]);

  const closeFacilityDialog = () => {
    setFacilityDialogOpen(false);
  };

  const openFacilityDialog = () => {
    setFacilityDialogOpen(true);
  };

  const [overlayImage, setOverlayImage] = useState('');
  const [screenshotMode, setScreenshotMode] = useState(false);

  const fileInputRef = useRef(null);
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setOverlayImage(e.target.result);
        setScreenshotMode(true);
        document.body.classList.add('screenshotMode');
        const keydownHandler = (event) => {
          if (event.key === 'Escape' || event.key === 'Delete') {
            document.body.classList.remove('screenshotMode');
            setScreenshotMode(false);
            document.removeEventListener('keydown', keydownHandler);
          }
        };
        document.addEventListener('keydown', keydownHandler);
        setTimeout(() => window.print(), 600);
        setTimeout(() => {
          if (document.body.classList.contains('screenshotMode')) {
            alert(
              'Press escape(ESC) or delete (DEL) on your keyboard to exit the screenshot mode',
            );
          }
        }, 5000);
      };
      reader.readAsDataURL(file);
    }
  };

  // Use the hook to load the Google Maps API only once per mount
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading Maps...</div>;

  return (
    <DashboardLayout facilityId={facilityId} isLoading={isLoading}>
      <DevelopmentOnly fallback={<p>This module is under development.</p>}>
        <div className={styles.transportationPage}>
          <div className={styles.transportationHeader}>
            <div className={styles.actionsContainer}>
              <span className={styles.actionsButtonContainer}>
                <Button
                  className={`${viewMode === 'list' ? styles.active : ''}`}
                  variant="text"
                  onClick={() => setViewMode('list')}
                >
                  List
                </Button>
              </span>
              <span className={styles.actionsButtonContainer}>
                <Button
                  className={`${viewMode === 'calendar' ? styles.active : ''}`}
                  variant="text"
                  onClick={() => setViewMode('calendar')}
                >
                  Calendar
                </Button>
              </span>
              <span className={styles.actionsButtonContainer}>
                <Button
                  className={`${viewMode === 'billing' ? styles.active : ''}`}
                  variant="text"
                  onClick={() => setViewMode('billing')}
                >
                  Billing
                </Button>
              </span>
            </div>

            <div className={styles.adminActions}>
              {canEdit && (
                <Button variant="outlined" onClick={openFacilityDialog}>
                  Manage Facility
                </Button>
              )}
              {canEdit && (
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={<Add />}
                  onClick={() => openDialog()}
                  disabled={!facilitySettings}
                  sx={{
                    backgroundColor: !facilitySettings ? 'gray' : undefined,
                    '&:hover': {
                      backgroundColor: !facilitySettings ? 'gray' : undefined,
                    },
                  }}
                >
                  New Transportation
                </Button>
              )}
            </div>
          </div>

          {viewMode === 'list' && (
            <div className={styles.transportationList}>
              {sortedUpcomingDays.length > 0 ? (
                sortedUpcomingDays.map((dayKey) => (
                  <div key={dayKey}>
                    <h2>{dayKey}</h2>
                    {groupedUpcomingAppointments[dayKey].map((appt) => (
                      <Box
                        className={styles.transportationListItem}
                        key={appt.id}
                        sx={{ cursor: canEdit ? 'pointer' : 'default' }}
                        onClick={() => canEdit && openDialog(appt)}
                      >
                        <strong className={styles.transportationListItemTime}>
                          {dayjs(appt.time.toDate()).format('h:mm a')}
                        </strong>
                        <strong>{appt.residentName}</strong>
                        <div>{appt.reason}</div>
                        <div className={styles.transportationListItemAddress}>
                          {appt.destination?.address || ''}
                        </div>
                        <div>{appt.primaryInsurance}</div>
                        <div>{appt.selectedOption}</div>
                        <div className={styles.createdBy}>
                          Created by:{' '}
                          {appt.createdBy ? appt.createdBy : 'Unknown'}
                        </div>
                      </Box>
                    ))}
                  </div>
                ))
              ) : (
                <p>No scheduled upcoming transportations</p>
              )}
            </div>
          )}

          {viewMode === 'billing' && (
            <div
              className={`${styles.transportationList} ${styles.billingView}`}
            >
              {sortedUpcomingDays.length > 0 ? (
                sortedUpcomingDays.map((dayKey) => (
                  <div key={dayKey}>
                    <h2>{dayKey}</h2>
                    {groupedUpcomingAppointments[dayKey].map((appt) => (
                      <Box
                        className={styles.transportationListItem}
                        key={appt.id}
                        sx={{ p: 2, cursor: canEdit ? 'pointer' : 'default' }}
                        onClick={() => canEdit && openDialog(appt)}
                      >
                        <strong className={styles.transportationListItemTime}>
                          {dayjs(appt.time.toDate()).format('h:mm a')}
                        </strong>
                        <div>
                          <strong>{appt.residentName}</strong>
                        </div>
                        <div>{appt.primaryInsurance}</div>
                        <div>{appt.reason}</div>
                        <div>{appt.selectedOption}</div>
                        <div style={{ maxWidth: '60px' }}>
                          {appt.transportationType === 'wheelchair'
                            ? 'WC'
                            : appt.transportationType === 'stretcher'
                              ? 'Cot'
                              : ''}
                        </div>
                      </Box>
                    ))}
                  </div>
                ))
              ) : (
                <p>No scheduled upcoming transportations</p>
              )}
            </div>
          )}

          {viewMode === 'calendar' && (
            <>
              <div className={styles.calendarNavContainer}>
                <div className={styles.monthNav}>
                  <IconButton
                    onClick={() =>
                      setCurrentMonth(currentMonth.subtract(1, 'month'))
                    }
                  >
                    <ArrowForwardIos sx={{ transform: 'rotate(180deg)' }} />
                  </IconButton>
                  <IconButton
                    onClick={() =>
                      setCurrentMonth(currentMonth.add(1, 'month'))
                    }
                  >
                    <ArrowForwardIos />
                  </IconButton>
                  <span>{currentMonth.format('MMMM YYYY')}</span>
                </div>

                <div className={styles.calendarNavScreenshot}>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    accept="image/*"
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                  />

                  <Button
                    type="button"
                    className="screenshotButton"
                    variant="outlined"
                    startIcon={<CameraAltIcon />}
                    onClick={() => fileInputRef.current.click()}
                  >
                    Overlay
                  </Button>

                  <Button
                    type="button"
                    className="screenshotButton"
                    variant="outlined"
                    startIcon={<CameraAltIcon />}
                    onClick={() => {
                      document.body.classList.add('screenshotMode');
                      const keydownHandler = (event) => {
                        if (event.key === 'Escape' || event.key === 'Delete') {
                          document.body.classList.remove('screenshotMode');
                          document.removeEventListener(
                            'keydown',
                            keydownHandler,
                          );
                        }
                      };
                      document.addEventListener('keydown', keydownHandler);
                      setTimeout(() => window.print(), 600);
                      setTimeout(() => {
                        if (
                          document.body.classList.contains('screenshotMode')
                        ) {
                          alert(
                            'Press escape(ESC) or delete (DEL) on your keyboard to exit the screenshot mode',
                          );
                        }
                      }, 5000);
                    }}
                  >
                    No Overlay
                  </Button>
                </div>
              </div>

              <div className={styles.calendarMonth}>
                <div className={styles.monthHeader}>Sunday</div>
                <div className={styles.monthHeader}>Monday</div>
                <div className={styles.monthHeader}>Tuesday</div>
                <div className={styles.monthHeader}>Wednesday</div>
                <div className={styles.monthHeader}>Thursday</div>
                <div className={styles.monthHeader}>Friday</div>
                <div className={styles.monthHeader}>Saturday</div>

                {monthDays.map((day, index) => (
                  <div
                    key={index}
                    className={`${styles.monthDay} ${
                      day.isSame(currentMonth, 'month') ? '' : 'outOfMonth'
                    } ${day.isSame(dayjs(), 'day') ? 'highlight-today' : ''}`}
                    onClick={() => {
                      if (canEdit) {
                        openDialog({ date: day, time: day });
                      }
                    }}
                  >
                    <div className={styles.monthDayNumber}>{day.date()}</div>
                    {appointmentsByDate[day.format('YYYY-MM-DD')] &&
                      appointmentsByDate[day.format('YYYY-MM-DD')].map(
                        (appt) => (
                          <Box
                            key={appt.id}
                            className={styles.monthEvent}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (canEdit) openDialog(appt);
                            }}
                          >
                            <span className={styles.monthEventTime}>
                              {dayjs(appt.time.toDate()).format('h:mm a')}
                            </span>{' '}
                            - <span>{appt.residentName}</span>
                          </Box>
                        ),
                      )}
                  </div>
                ))}
              </div>
            </>
          )}

          {dialogOpen && facilitySettings && (
            <TransportationDialog
              open={dialogOpen}
              dialogMode={dialogMode}
              formData={dialogFormData}
              setFormData={setDialogFormData}
              onClose={closeDialog}
              onCreateOrUpdate={handleCreateOrUpdate}
              onDelete={handleDelete}
              facilityLocation={facilitySettings.location}
              facilitySettings={facilitySettings}
            />
          )}

          <FacilitySettingsDialog
            open={facilityDialogOpen}
            onClose={closeFacilityDialog}
            onSave={() => {}}
            facilitySettings={facilitySettings}
            facilityId={facilityId}
          />
        </div>
      </DevelopmentOnly>
    </DashboardLayout>
  );
}

export default Transportation;
